import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, LogOut, X } from 'lucide-react';
import { selectCartTotalQuantity, toggleCart } from '../store/slices/cartSlice';
import { setSearchQuery, clearFilters } from '../store/slices/filtersSlice';
import { selectIsAuthenticated, selectUser, logoutAdmin } from '../store/slices/authSlice';
import Logo from '../assets/Logo.png';

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const totalQuantity = useSelector(selectCartTotalQuantity);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);

  const [searchValue, setSearchValue] = useState('');
  const debounceRef = useRef(null);

  const handleSearchChange = (val) => {
    setSearchValue(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(setSearchQuery(val));
    }, 400);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    dispatch(setSearchQuery(''));
  };

  const handleLogoClick = () => {
    setSearchValue('');
    dispatch(clearFilters());
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutAdmin()).unwrap();
      navigate('/admin/login');
    } catch (_) {}
  };

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  return (
    <header className="hidden md:block bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex-shrink-0 flex items-center">
            <img src={Logo} alt="Store" className="h-10 w-auto" />
          </Link>

          {/* Search */}
          <div className="flex flex-1 max-w-lg mx-6">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                value={searchValue}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 rounded-full bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              />
              {searchValue && (
                <button
                  onClick={handleSearchClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <>
                <span className="text-sm text-gray-600">
                  Hi, <span className="font-medium text-gray-900">{user?.username}</span>
                </span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-red-600 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            )}

            <button
              onClick={() => dispatch(toggleCart())}
              className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label="Shopping cart"
            >
              <ShoppingBag className="w-6 h-6" />
              {totalQuantity > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-0.5 leading-none">
                  {totalQuantity > 99 ? '99+' : totalQuantity}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
